'use client';

import { useEffect, useState } from 'react';

interface OEmbedResponse {
  type: string;
  version: string;
  title?: string;
  html?: string;
  width?: number;
  height?: number;
  provider_name?: string;
  provider_url?: string;
}

const determineProvider = (url: string): string => {
  // oEmbed 엔드포인트
  const providers = {
    youtube: {
      patterns: [
        /https?:\/\/(?:www\.)?youtube\.com\/watch\?.*v=([^&]+)/,
        /https?:\/\/(?:www\.)?youtube\.com\/embed\/([^/?]+)/,
        /https?:\/\/youtu\.be\/([^/?]+)/,
        /https?:\/\/(?:www\.)?youtube\.com\/shorts\/([^/?]+)/,
      ],
      endpoint: 'https://www.youtube.com/oembed',
    },
    vimeo: {
      patterns: [
        /https?:\/\/(?:www\.)?vimeo\.com\/(\d+)/,
        /https?:\/\/player\.vimeo\.com\/video\/(\d+)/,
      ],
      endpoint: 'https://vimeo.com/api/oembed.json',
    },
    twitter: {
      patterns: [/https?:\/\/(?:www\.)?twitter\.com\/\w+\/status\/\d+/],
      endpoint: 'https://publish.twitter.com/oembed',
    },
  };

  // URL과 매칭되는 제공자 찾기
  for (const [provider, config] of Object.entries(providers)) {
    const isMatch = config.patterns.some((pattern) => pattern.test(url));
    if (isMatch) {
      return config.endpoint;
    }
  }

  throw new Error('Unsupported provider');
};

const OEmbed = ({ url }: { url: string }) => {
  const [embedHtml, setEmbedHtml] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOembed = async () => {
      try {
        const provider = determineProvider(url);
        const response = await fetch(
          `${provider}?url=${encodeURIComponent(url)}`,
          {
            headers: {
              Accept: 'application/json',
            },
          }
        );
        const data: OEmbedResponse = await response.json();

        if (data.html) {
          // iframe 크기 조정을 위한 HTML 수정
          const modifiedHtml = data.html.replace(
            /<iframe/g,
            '<iframe style="width: 100%; height: 100%; position: absolute; top: 0; left: 0;"'
          );
          setEmbedHtml(modifiedHtml);
        }
      } catch (error) {
        console.error(
          JSON.stringify({
            'error-log': {
              message: 'oEmbed 로딩 실패',
              error: JSON.stringify(error),
            },
          })
        );
      } finally {
        setLoading(false);
      }
    };

    if (url) {
      fetchOembed();
    }
  }, [url]);

  // TODO: 적절한 로딩 UI 추가
  if (loading) {
    return <div className="h-[300px] w-full animate-pulse bg-w-gray-100" />;
  }

  return (
    <div className="relative w-full">
      <div
        className="relative mx-auto my-[5px] aspect-video w-full min-w-[200px] max-w-[600px]"
        dangerouslySetInnerHTML={{ __html: embedHtml }}
      />
    </div>
  );
};

export default OEmbed;
